
import { mapGetters } from 'vuex'
import { getExternalUrl } from '~/utils/get-external-url'
import { types } from '~/utils/product-item-type'

import WProductHeader from '@/components/global/w-product-header/w-product-header.vue'

import { createChartSeries } from '@/utils/chart-series'

export default {
  components: {
    WProductHeader,
  },
  inheritAttrs: false,
  props: {
    producerLogo: {
      type: String,
      required: false,
      default: null,
    },
    producerCaskLogo: {
      type: String,
      required: false,
      default: null,
    },
    itemType: {
      type: String,
      default: types.Bottle,
    },
    articleDetails: {
      type: Object,
      required: true,
    },
    priceDevelopment: {
      type: Array,
      required: true,
      default: () => [],
    },
    projectedDevelopment: {
      type: Array,
      required: true,
      default: () => [],
    },
    swexOtcs: {
      type: Array,
      required: true,
      default: () => [],
    },
    swexTrades: {
      type: Array,
      required: true,
      default: () => [],
    },
    compoundProfit: {
      type: [String, Number],
      default: null,
    },
    availabilityOnWwi: {
      type: [String, Number],
      default: null,
    },
    swexMarketPrice: {
      type: [String, Number],
      default: null,
    },
    lastOtcTradePrice: {
      type: [String, Number],
      default: null,
    },
    distilleryRating: {
      type: [String, Number],
      default: null,
    },
    firstTradePrice: {
      type: [String, Number],
      default: null,
    },
    firstSoldPrice: {
      type: [String, Number],
      default: null,
    },
    lastTradePrice: {
      type: [String, Number],
      default: null,
    },
    lastTradeDate: {
      type: String,
      default: null,
    },
    firstTradeDate: {
      type: String,
      default: null,
    },
    firstSoldDate: {
      type: String,
      default: null,
    },
    orders: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      tradingCasksUrl: getExternalUrl('cask-trading', this.$i18n.locale),
    }
  },

  computed: {
    ...mapGetters('trader', ['permissions']),

    isBottle() {
      return this.$isBottle({ itemType: this.itemType })
    },

    isCask() {
      return this.$isCask({ itemType: this.itemType })
    },

    showGraph() {
      return this.priceDevelopment.length > 0 
      || this.projectedDevelopment.length > 0 
      || this.swexTrades.length > 0
    },

    chartSeries() {
      const series = []

      if (this.priceDevelopment.length) {
        series.push(
          createChartSeries(
            this.priceDevelopment,
            this.$gettext('MarketPrice'), {
              type: (this.priceDevelopment.length != 1) ? 'line' : 'scatter',
            }
          ),
        )
      }

      if (this.swexOtcs.length) {
        series.push(
          createChartSeries(this.swexOtcs, this.$gettext('OtcTransaction'), {
            type: 'scatter',
          }),
        )
      }

      if (this.swexTrades.length) {
        series.push(
          createChartSeries(this.swexTrades, this.$gettext('Trades'), {
            type: 'scatter',
          }),
        )
      }

      if (this.projectedDevelopment.length) {
        series.push(
          createChartSeries(
            this.projectedDevelopment,
            this.$gettext('Projected price'),
            {
              dashStyle: 'dash',
              color: 'var(--swi-body-text-color-secondary)',
            },
          ),
        )
      }

      return series
    },

    productHeaderProps() {
      const { producerCaskLogo, details, articleDetails } = this
      const { imageUrl, brandName, displayName, age } = articleDetails
      const { isBottle } = this

      const props = {
        imageUrl: isBottle ? imageUrl : producerCaskLogo,
        dispayName: isBottle ? brandName : displayName,
        additionalText: this.$gettext('%syo', isBottle ? age: articleDetails.maturationAge),
        details,
      }

      return props
    },

    details() {
      return [
        // Bottle properties
        this.articleDetails.batchName && {
          name: this.$gettext('Batch name'),
          value: this.articleDetails.batchName,
        },

        this.articleDetails.yearDistillation && {
          name: this.$gettext('Year distillation'),
          value: this.articleDetails.yearDistillation,
        },

        this.articleDetails.yearBottled && {
          name: this.$gettext('Year bottling'),
          value: this.articleDetails.yearBottled,
        },

        this.articleDetails.bottleryName && {
          name: this.$gettext('Bottler'),
          value: this.articleDetails.bottleryName,
        },

        this.articleDetails.alcoholPercentage && {
          name: this.$gettext('Alcohol percentage'),
          value: this.$formatAsPercentage(
            this.articleDetails.alcoholPercentage,
          ),
        },

        this.articleDetails.volumeInMl && {
          name: this.$gettext('Volume'),
          value: this.$formatAsAmount(this.articleDetails.volumeInMl),
        },

        // Cask properties
        this.articleDetails.distillationYear && {
          name: this.$gettext('Year distillation'),
          value: this.articleDetails.distillationYear,
        },

        this.articleDetails.caskType && {
          name: this.$gettext('Cask type'),
          value: this.articleDetails.caskType,
        },

        this.articleDetails.originalContent && {
          name: this.$gettext('Original content'),
          value: this.articleDetails.originalContent,
        },

        this.articleDetails.distilleryRating && {
          name: this.$gettext('Distillery rating'),
          value: this.articleDetails.distilleryRating,
        },
      ].filter(Boolean)
    },

    sellOrdersSelection() {
      return this.orders.sellOrders.slice(0, 3)
    },

    transactionsLink() {
      return this.$nuxt.context.app.localePath(
        `/whisky/${this.$route.params.slug}/transactions`,
      )
    },
  },

  methods: {
    dateFormatter(date) {
      return this.$dayjs(date).format("MMM 'YY")
    },
    formatNote(source) {
      const {
        remainingYearsTillMaturation,
        expectedBottlingYear,
        expectedBottlingYearMin,
        expectedBottlingYearMax,
      } = this.articleDetails

      return source
        .replace('<<@Variable TBA>>', remainingYearsTillMaturation)
        .replace('<<@Variable TBA_Year>>', expectedBottlingYear)
        .replace('<<@Variable Min_TBA>>', expectedBottlingYearMin)
        .replace('<<@Variable Max_TBA>>', expectedBottlingYearMax)
    },
  },
}
